// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-2268 · «¿EN QUÉ GABINETE ESTÁS HOY?» Y LA FICHA QUE SE ABRE SOLA AL PASAR A GABINETE   (t530, 28-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «cuando la cita se pone en estado de En gabinete, que la ficha del paciente se abra en la pestaña de Historial
//  … debe haber alguna manera que el sistema sepa que usuario al abrir su cuenta en un ordenador en que gabinete está
//  trabajando, algo tipo, a que gabinete estas asignado/a hoy?».
//
//  Montado UNA vez para toda la app (App.jsx, por encima de las rutas). Solo en un ORDENADOR (utils/dispositivo: en el
//  móvil y en la tableta no hace nada):
//   · al entrar por primera vez en el día, pregunta en qué gabinete trabaja hoy, con los gabinetes de su sede, el propuesto
//     marcado (donde tiene más citas hoy) y «Ninguno (recepción, administración…)»; con «Ninguno», «No volver a preguntar en
//     este ordenador». Se recuerda en este ordenador ese día y se cambia desde el menú de usuario («Mi gabinete hoy»,
//     MiGabineteEnMenu). Y el ajuste de cada persona «Abrir la ficha sola al pasar a gabinete».
//   · cuando el servidor dice que un paciente ha pasado al gabinete de este ordenador (por la conexión de las llamadas:
//     context/LlamadasContext lo reparte como 'biango-gabinete-paciente'), abre su ficha en Historial; si hay algo a medio
//     escribir (utils/sinGuardar) o la persona apagó el ajuste, sale un aviso en la esquina, «Lucía P. ha pasado a
//     Gabinete 2 · Abrir ficha». Y lanza en la ventana 'biango:paciente-en-gabinete' con { citaId, pacienteId, gabinete,
//     abierta } (lo escucha la pregunta «¿Grabar consulta?», t529).
//  Las decisiones, en utils/puestoGabinete (puras); el servidor, en backend/services/pacienteEnGabinete.js.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { clinicaDeSesion, pestanaDeOtraClinica } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Ico } from './ui'
import { t } from '../utils/i18n'
import { idDelDispositivo, tipoDeDispositivo } from '../utils/dispositivo'
import { hayAlgoSinGuardar, vigilarLoQueSeEscribe } from '../utils/sinGuardar'
import {
  claveLocal, leerPuesto, guardarPuesto, nuncaPreguntar, queHacerAlEntrar, queHacerAlLlegar, urlFichaHistorial, estaEnEsaFicha,
  detalleDelEvento, estaEnSuHistorial, motivoSinAbrir,
} from '../utils/puestoGabinete'
import { leerEstadoPuesto, ponerEstadoPuesto, suscribirPuesto, eventoElegirGabinete } from '../utils/puestoGabineteEstado'

export const EVENTO_PACIENTE_EN_GABINETE = 'biango:paciente-en-gabinete'
const RECLAMO = 'biango.puestoGabinete.reclamo'
const MIRAR_CADA_MS = 30 * 60 * 1000
const ESPERA_PREGUNTA_MS = 2500

// ── EL ESTADO DE ESTE ORDENADOR, COMPARTIDO CON EL MENÚ (utils/MiGabineteEnMenu) ────────────────────
const ponerEstado = ponerEstadoPuesto
const useEstadoPuesto = () => useSyncExternalStore(suscribirPuesto, leerEstadoPuesto, leerEstadoPuesto)

const almacen = () => { try { return window.localStorage } catch (_) { return null } }
const TAB = Math.random().toString(36).slice(2)

// Una pestaña por ordenador hace el salto: la que está a la vista; si ninguna, la primera que lo pida.
function reclamar(citaId) {
  const ahora = Date.now()
  try {
    const r = JSON.parse(localStorage.getItem(RECLAMO) || 'null')
    if (r && r.cita === citaId && r.tab !== TAB && ahora - r.at < 30000) return false
    localStorage.setItem(RECLAMO, JSON.stringify({ cita: citaId, tab: TAB, at: ahora }))
  } catch (_) { /* sin almacenamiento: esta pestaña lo hace */ }
  return true
}

// (3-nov-2026) Las pantallas PÚBLICAS (las que se abren con un enlace, sin entrar en Biango: descargar Biango Migrar, un pago,
// una firma, el portal, la tableta…) no preguntan el gabinete aunque el navegador tenga abierta la sesión de alguien del equipo.
// Lo vio el dueño al abrir en el ordenador de una clínica el enlace corto de Biango Migrar (/m/…): le salía «¿En qué gabinete
// estás hoy?» encima de la descarga. Son las rutas públicas de App.jsx.
const PANTALLA_PUBLICA = /^\/(m|a|pago|portal|login|tablet|anestesista|cambiar-cuenta|configurar|consentimiento|crear-pin|derivar|empezar|encuesta|firma-biango|intake|invitacion|olvide-mi-contrasena|presupuesto|referidor|restablecer|revision-historia|rgpd|rx|sala|soporte-local)(\/|$)/
export function esPantallaPublica(ruta) { return PANTALLA_PUBLICA.test(String(ruta || '')) }

export default function PuestoGabinete() {
  const { user } = useAuth() || {}
  const navigate = useNavigate()
  const st = useEstadoPuesto()
  const [preguntando, setPreguntando] = useState(null)   // { propuesto, desdeMenu }
  const [avisos, setAvisos] = useState([])
  const clinica = (() => { try { return clinicaDeSesion() } catch (_) { return null } })()
  const clave = claveLocal(clinica, user && user.id)
  const ordenador = (() => { try { return tipoDeDispositivo() === 'ordenador' } catch (_) { return false } })()
  const dispositivo = ordenador ? idDelDispositivo() : null
  const ref = useRef({})
  ref.current = { clave, dispositivo, navigate }

  // Lo que se teclea, para saber si hay algo a medio escribir.
  useEffect(() => vigilarLoQueSeEscribe(), [])

  // ── AL ENTRAR (y cada media hora, y al volver a la pantalla: el día cambia con el ordenador encendido) ──
  useEffect(() => {
    if (!user || !user.id || !ordenador || !dispositivo || !clave) { ponerEstado({ listo: false }); return undefined }
    let vivo = true
    let temporizador = null
    const mirar = async () => {
      let d = null
      try { d = (await api.get('/puesto-gabinete', { params: { dispositivo } })).data || null } catch (_) { d = null }
      if (!vivo || !d) return
      const local = leerPuesto(almacen(), clave)
      const q = queHacerAlEntrar({ datos: d, local, nunca: nuncaPreguntar(almacen(), clave) })
      const nombreDe = (id) => ((d.gabinetes || []).find((g) => Number(g.id) === Number(id)) || {}).nombre || null
      ponerEstado({ listo: q.accion !== 'nada', datos: d, abrirFicha: d.abrir_ficha !== false })
      if (q.accion === 'usar' || q.accion === 'reenviar' || q.accion === 'ninguno') {
        // t551 · B-2371 · con los otros gabinetes de hoy, si los hay.
        const otros = Array.isArray(q.otros) ? q.otros.map((o) => ({ gabinete_id: o.gabinete_id, nombre: nombreDe(o.gabinete_id) || o.nombre || null })) : []
        const actual = { gabinete_id: q.gabinete_id, nombre: q.gabinete_id == null ? null : nombreDe(q.gabinete_id), ...(otros.length ? { otros } : {}) }
        ponerEstado({ actual })
        guardarPuesto(almacen(), clave, { fecha: d.hoy, ...actual })
        if (q.accion !== 'usar') api.put('/puesto-gabinete', { dispositivo, gabinete_id: q.gabinete_id, ...(otros.length ? { otros_gabinetes: otros.map((o) => o.gabinete_id) } : {}) }).catch(() => {})
      } else if (q.accion === 'preguntar') {
        ponerEstado({ actual: null })
        // Sin taparse con otra pregunta de entrada (la de la huella o la cara): espera a que se cierre.
        const sacar = (intentos) => {
          if (!vivo) return
          const otra = typeof document !== 'undefined' && document.querySelector('[data-oferta-huella-o-cara]')
          if (otra && intentos < 30) { temporizador = setTimeout(() => sacar(intentos + 1), 2000); return }
          setPreguntando({ propuesto: q.propuesto, desdeMenu: false })
        }
        temporizador = setTimeout(() => sacar(0), ESPERA_PREGUNTA_MS)
      } else {
        ponerEstado({ actual: null })
      }
    }
    let ultimo = Date.now()
    const mirarYApuntar = () => { ultimo = Date.now(); mirar() }
    mirar()
    const cada = setInterval(mirarYApuntar, MIRAR_CADA_MS)
    // Al volver a la pantalla tras un buen rato (el ordenador que se queda encendido de un día para otro), se vuelve a mirar.
    const alVolver = () => { if (document.visibilityState === 'visible' && Date.now() - ultimo > 10 * 60 * 1000) mirarYApuntar() }
    document.addEventListener('visibilitychange', alVolver)
    return () => { vivo = false; clearInterval(cada); clearTimeout(temporizador); document.removeEventListener('visibilitychange', alVolver) }
  }, [user && user.id, clinica, ordenador, dispositivo])

  // ── «Mi gabinete hoy» desde el menú de usuario ──
  useEffect(() => {
    const f = () => {
      const e = leerEstadoPuesto()
      if (e.listo) setPreguntando({ propuesto: (e.actual && e.actual.gabinete_id) || (e.datos && e.datos.propuesto) || null, desdeMenu: true })
    }
    window.addEventListener(eventoElegirGabinete(), f)
    return () => window.removeEventListener(eventoElegirGabinete(), f)
  }, [])

  // ── EL PACIENTE HA PASADO A ESTE GABINETE ──
  useEffect(() => {
    if (!ordenador) return undefined
    const actuar = (aviso) => {
      const accion = queHacerAlLlegar({
        actual: leerEstadoPuesto().actual, abrirFicha: leerEstadoPuesto().abrirFicha, aviso,
        enEsaFicha: estaEnEsaFicha(window.location.pathname, window.location.search, aviso.paciente_id),
        // t562 · con su ficha ya abierta en otra pestaña, se pasa a su Historial.
        enSuHistorial: estaEnSuHistorial(window.location.pathname, window.location.search, aviso.paciente_id),
        sinGuardar: hayAlgoSinGuardar(),
      })
      if (accion === 'nada') return
      if (accion === 'abrir') ref.current.navigate(urlFichaHistorial(aviso.paciente_id))
      if (accion === 'aviso') setAvisos((l) => [{ ...aviso, _k: String(aviso.cita_id) + ':' + Date.now() }, ...l.filter((x) => x.cita_id !== aviso.cita_id)].slice(0, 3))
      try { window.dispatchEvent(new CustomEvent(EVENTO_PACIENTE_EN_GABINETE, { detail: detalleDelEvento(aviso, accion) })) } catch (_) { /* nada */ }
    }
    const f = (e) => {
      const aviso = (e && e.detail) || {}
      if (!aviso.cita_id) return
      // t562 · una pestaña que se ha quedado en OTRA clínica (t548: en otra pestaña se ha entrado en otra) no lo hace ni lo
      // reclama: lo haría con la sesión que no es y le quitaría el salto a la pestaña buena de este ordenador.
      try { if (typeof pestanaDeOtraClinica === 'function' && pestanaDeOtraClinica()) return } catch (_) { /* sin saberlo, sigue */ }
      if (document.visibilityState === 'visible') { if (reclamar(aviso.cita_id)) actuar(aviso); return }
      setTimeout(() => { if (reclamar(aviso.cita_id)) actuar(aviso) }, 1200)
    }
    window.addEventListener('biango-gabinete-paciente', f)
    return () => window.removeEventListener('biango-gabinete-paciente', f)
  }, [ordenador])

  // Los avisos de la esquina se van solos a los 10 minutos.
  useEffect(() => {
    if (!avisos.length) return undefined
    const t0 = setTimeout(() => setAvisos((l) => l.slice(0, -1)), 10 * 60 * 1000)
    return () => clearTimeout(t0)
  }, [avisos])

  // ── t562 · B-2386 · A QUIEN HA PASADO LA CITA: «NO SE HA ABIERTO EN NINGÚN ORDENADOR», CON EL PORQUÉ ──
  // En cualquier dispositivo (también el móvil del modo auxiliar): lo manda el servidor (services/pacienteEnGabinete ·
  // gabinete:sin-abrir) solo si en la clínica hoy algún ordenador ha dicho su gabinete. Se va solo al minuto.
  const [sinAbrir, setSinAbrir] = useState([])
  useEffect(() => {
    const f = (e) => {
      const d = (e && e.detail) || {}
      if (!motivoSinAbrir(d)) return
      try { if (typeof pestanaDeOtraClinica === 'function' && pestanaDeOtraClinica()) return } catch (_) { /* sigue */ }
      setSinAbrir((l) => [{ ...d, _k: String(d.cita_id) + ':' + Date.now() }, ...l.filter((x) => x.cita_id !== d.cita_id)].slice(0, 2))
    }
    window.addEventListener('biango-gabinete-sin-abrir', f)
    return () => window.removeEventListener('biango-gabinete-sin-abrir', f)
  }, [])
  useEffect(() => {
    if (!sinAbrir.length) return undefined
    const t0 = setTimeout(() => setSinAbrir((l) => l.slice(0, -1)), 60 * 1000)
    return () => clearTimeout(t0)
  }, [sinAbrir])
  const textoSinAbrir = (d) => {
    const gab = (d.gabinete && d.gabinete.nombre) || null
    const v = { paciente: d.paciente || t('components.PuestoGabinete.el_paciente', 'El paciente'), gabinete: gab }
    switch (motivoSinAbrir(d)) {
      case 'sin_ficha': return gab
        ? t('components.PuestoGabinete.sin_abrir_sin_ficha', '{{paciente}} ha pasado a {{gabinete}}, pero la cita no está enlazada a ninguna ficha: no hay ficha que abrir.', v)
        : t('components.PuestoGabinete.sin_abrir_sin_ficha_2', '{{paciente}} ha pasado al $t(voc.gabinete), pero la cita no está enlazada a ninguna ficha: no hay ficha que abrir.', v)
      case 'sin_gabinete': return t('components.PuestoGabinete.sin_abrir_sin_gabinete', 'La ficha de {{paciente}} no se ha abierto en ningún ordenador: su cita no tiene $t(voc.gabinete) (su agenda no tiene uno asignado) y su profesional no ha dicho hoy en qué $t(voc.gabinete) está.', v)
      case 'sin_ordenador': return t('components.PuestoGabinete.sin_abrir_sin_ordenador', 'La ficha de {{paciente}} no se ha abierto en ningún ordenador: ninguno ha dicho hoy que está en {{gabinete}}.', v)
      case 'sin_permiso_o_sede': return gab
        ? t('components.PuestoGabinete.sin_abrir_sin_permiso', 'La ficha de {{paciente}} no se ha abierto: los ordenadores de {{gabinete}} son de otra sede o de quien no puede ver el historial.', v)
        : t('components.PuestoGabinete.sin_abrir_sin_permiso_2', 'La ficha de {{paciente}} no se ha abierto: los ordenadores a los que iba son de otra sede o de quien no puede ver el historial.', v)
      case 'sin_conexion': return gab
        ? t('components.PuestoGabinete.sin_abrir_sin_conexion', 'El ordenador de {{gabinete}} no está conectado ahora mismo: la ficha de {{paciente}} se abrirá allí si vuelve a conectarse en los próximos minutos.', v)
        : t('components.PuestoGabinete.sin_abrir_sin_conexion_2', 'El ordenador al que iba no está conectado ahora mismo: la ficha de {{paciente}} se abrirá allí si vuelve a conectarse en los próximos minutos.', v)
      default: return ''
    }
  }
  const avisosSinAbrir = sinAbrir.length > 0 && (
    <div data-avisos-sin-abrir="" style={{ position: 'fixed', left: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 99980, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'min(380px, calc((100vw / var(--z, 1)) - 32px))' }}>
      {sinAbrir.map((d) => (
        <div key={d._k} data-aviso-sin-abrir={d.motivo} role="status"
          style={{ background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderLeft: '4px solid var(--warning)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.18)', padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ color: 'var(--warning)', display: 'inline-flex', marginTop: 1 }}><Ico n="monitor" s={18} /></span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4 }}>{textoSinAbrir(d)}</span>
          <button type="button" aria-label={t('components.PuestoGabinete.cerrar_aviso', 'Cerrar el aviso')} onClick={() => setSinAbrir((l) => l.filter((x) => x._k !== d._k))}
            style={{ padding: 4, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}>
            <Ico n="close" s={16} />
          </button>
        </div>
      ))}
    </div>
  )

  if (!user) return null
  if (esPantallaPublica(typeof window !== 'undefined' && window.location ? window.location.pathname : '')) return null
  if (!ordenador) return avisosSinAbrir || null
  return (
    <>
      {avisosSinAbrir}
      {preguntando && st.datos && (
        <PreguntaGabinete datos={st.datos} propuesto={preguntando.propuesto} desdeMenu={preguntando.desdeMenu}
          actual={st.actual} abrirFicha={st.abrirFicha !== false}
          onListo={async ({ gabineteId, otros = [], nunca, abrirFicha }) => {
            const d = st.datos
            const nombreDe = (id) => ((d.gabinetes || []).find((g) => Number(g.id) === Number(id)) || {}).nombre || null
            const nombre = gabineteId == null ? null : nombreDe(gabineteId)
            // t551 · B-2371 · los otros gabinetes en los que también está hoy (una auxiliar que va alternando).
            const losOtros = gabineteId == null ? [] : otros.filter((id) => Number(id) !== Number(gabineteId)).map((id) => ({ gabinete_id: Number(id), nombre: nombreDe(id) }))
            const actual = { gabinete_id: gabineteId, nombre, ...(losOtros.length ? { otros: losOtros } : {}) }
            guardarPuesto(almacen(), clave, { fecha: d.hoy, ...actual, nunca: gabineteId == null ? !!nunca : false })
            ponerEstado({ actual, abrirFicha })
            setPreguntando(null)
            try { await api.put('/puesto-gabinete', { dispositivo, gabinete_id: gabineteId, ...(d.varios ? { otros_gabinetes: losOtros.map((o) => o.gabinete_id) } : {}) }) } catch (_) { /* se vuelve a decir al entrar */ }
            if (abrirFicha !== (d.abrir_ficha !== false)) {
              try { await api.patch('/auth/preferencias', { gabinete_abrir_ficha: !!abrirFicha }); ponerEstado({ datos: { ...d, abrir_ficha: !!abrirFicha } }) } catch (_) { /* se queda en esta pantalla */ }
            }
          }}
          onCerrar={() => {
            // Cerrar sin contestar = «Ninguno» hoy en este ordenador (no abre nada); se cambia desde el menú.
            if (!preguntando.desdeMenu) {
              guardarPuesto(almacen(), clave, { fecha: st.datos.hoy, gabinete_id: null, nombre: null })
              ponerEstado({ actual: { gabinete_id: null, nombre: null } })
              api.put('/puesto-gabinete', { dispositivo, gabinete_id: null }).catch(() => {})
            }
            setPreguntando(null)
          }} />
      )}
      {avisos.length > 0 && (
        <div data-avisos-gabinete="" style={{ position: 'fixed', right: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 99980, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'min(360px, calc((100vw / var(--z, 1)) - 32px))' }}>
          {avisos.map((a) => (
            <div key={a._k} data-aviso-gabinete={a.cita_id} role="status"
              style={{ background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.18)', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--primary)', display: 'inline-flex' }}><Ico n="doctor" s={18} /></span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.35 }}>
                {a.gabinete && a.gabinete.nombre
                  ? t('components.PuestoGabinete.ha_pasado_a', '{{paciente}} ha pasado a {{gabinete}}', { paciente: a.paciente || t('components.PuestoGabinete.el_paciente', 'El paciente'), gabinete: a.gabinete.nombre })
                  : t('components.PuestoGabinete.ha_pasado', '{{paciente}} ha pasado a tu $t(voc.gabinete)', { paciente: a.paciente || t('components.PuestoGabinete.el_paciente', 'El paciente') })}
              </span>
              <button type="button" data-abrir-ficha-gabinete="" onClick={() => { setAvisos((l) => l.filter((x) => x._k !== a._k)); navigate(urlFichaHistorial(a.paciente_id)) }}
                style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('components.PuestoGabinete.abrir_ficha', 'Abrir ficha')}
              </button>
              <button type="button" aria-label={t('components.PuestoGabinete.cerrar_aviso', 'Cerrar el aviso')} onClick={() => setAvisos((l) => l.filter((x) => x._k !== a._k))}
                style={{ padding: 4, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}>
                <Ico n="close" s={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── LA PREGUNTA ───────────────────────────────────────────────────────────────────────────────────
// t551 · exportada para que la guardia la monte de verdad (services/auxiliar-en-varios-gabinetes.test.js §7).
export function PreguntaGabinete({ datos, propuesto, desdeMenu, actual, abrirFicha, onListo, onCerrar }) {
  const gabinetes = Array.isArray(datos.gabinetes) ? datos.gabinetes : []
  const inicial = desdeMenu && actual ? (actual.gabinete_id == null ? 'ninguno' : String(actual.gabinete_id)) : (propuesto != null ? String(propuesto) : '')
  const [elegido, setElegido] = useState(inicial)
  // t551 · B-2371 · los OTROS gabinetes en los que también está hoy: solo a una auxiliar, con la migración (`datos.varios`).
  const conVarios = datos.varios === true && datos.es_auxiliar === true
  const [otros, setOtros] = useState(() => (desdeMenu && actual && Array.isArray(actual.otros) ? actual.otros.map((o) => String(o.gabinete_id)) : []))
  const alternarOtro = (id) => setOtros((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]))
  const [nunca, setNunca] = useState(false)
  const [abrir, setAbrir] = useState(!!abrirFicha)
  const [ocupado, setOcupado] = useState(false)
  const conSede = !!datos.varias_sedes
  const opcion = (valor, texto, sub, marca) => {
    const si = elegido === valor
    return (
      <button key={valor} type="button" data-opcion-gabinete={valor} onClick={() => setElegido(valor)} aria-pressed={si}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
          border: '1.5px solid ' + (si ? 'var(--primary)' : 'var(--border)'), background: si ? 'var(--primary-light, var(--surface-2))' : 'var(--surface)', color: 'var(--text-primary)' }}>
        <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid ' + (si ? 'var(--primary)' : 'var(--border)'), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {si && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)' }} />}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{texto}</span>
          {sub && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</span>}
        </span>
        {marca && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{marca}</span>}
      </button>
    )
  }
  const listo = async () => {
    if (!elegido || ocupado) return
    setOcupado(true)
    const losOtros = conVarios && elegido !== 'ninguno' ? otros.filter((x) => x !== elegido).map(Number) : []
    // t559 · con «Ninguno» la casilla de abrir la ficha no se ve: su preferencia se queda como estaba (no se toca).
    try { await onListo({ gabineteId: elegido === 'ninguno' ? null : Number(elegido), otros: losOtros, nunca: elegido === 'ninguno' && nunca, abrirFicha: elegido === 'ninguno' ? !!abrirFicha : abrir }) } finally { setOcupado(false) }
  }
  return (
    <div className="modal-safe" data-pregunta-gabinete="" role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 99985, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: 14, width: 'min(440px, 100%)', maxHeight: 'calc(var(--alto-pantalla) - 32px - var(--sa-arriba) - var(--sa-abajo))', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.3)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ color: 'var(--primary)', display: 'inline-flex', marginTop: 2 }}><Ico n="monitor" s={22} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{t('components.PuestoGabinete.titulo', '¿En qué gabinete estás hoy?')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
              {t('components.PuestoGabinete.explica', 'Cuando un paciente pase a tu gabinete, su ficha se abrirá sola en este ordenador, en el Historial.')}
            </div>
            {/* t542 · B-2290 · a una auxiliar: lo que conteste la deja como auxiliar de las entradas médicas de ese gabinete. */}
            {datos.es_auxiliar === true && (
              <div data-gabinete-auxiliar="" style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                {t('components.PuestoGabinete.explica_auxiliar', 'Y las entradas médicas de las citas de ese gabinete se apuntarán contigo como auxiliar, sin tener que marcarlo.')}
              </div>
            )}
          </div>
          <button type="button" aria-label={t('components.PuestoGabinete.cerrar', 'Cerrar')} onClick={onCerrar}
            style={{ padding: 4, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}>
            <Ico n="close" s={18} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {gabinetes.map((g) => opcion(String(g.id), g.nombre, conSede && g.sede ? g.sede : null,
            propuesto != null && Number(propuesto) === Number(g.id) && !desdeMenu ? t('components.PuestoGabinete.tus_citas', 'Tus citas de hoy') : null))}
          {opcion('ninguno', t('components.PuestoGabinete.ninguno', 'Ninguno (recepción, administración…)'), t('components.PuestoGabinete.ninguno_sub', 'Este ordenador no abrirá ninguna ficha sola.'), null)}
        </div>
        {conVarios && elegido && elegido !== 'ninguno' && gabinetes.length > 1 && (
          <div data-otros-gabinetes="" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{t('components.PuestoGabinete.otros_titulo', '¿Vas alternando con otros gabinetes hoy?')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
              {t('components.PuestoGabinete.otros_explica', 'Márcalos también. En sus citas te pondrás sola de auxiliar si eres la única; si hay más, se elige en la entrada médica.')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {gabinetes.filter((g) => String(g.id) !== elegido).map((g) => (
                <label key={g.id} data-otro-gabinete={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={otros.includes(String(g.id))} onChange={() => alternarOtro(String(g.id))} />
                  <span>{g.nombre}{conSede && g.sede ? ' · ' + g.sede : ''}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {elegido === 'ninguno' && (
          <label data-no-volver-a-preguntar="" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={nunca} onChange={(e) => setNunca(e.target.checked)} />
            {t('components.PuestoGabinete.no_volver', 'No volver a preguntar en este ordenador')}
          </label>
        )}
        {/* t559 · con «Ninguno» (recepción, administración…) este ordenador no abre nada: la casilla no se enseña, para no
            confundir. Su valor se guarda en el estado: al volver a elegir un gabinete sale como estaba. */}
        {elegido !== 'ninguno' && (
          <label data-abrir-ficha-sola="" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary, var(--text-primary))' }}>
            <input type="checkbox" checked={abrir} onChange={(e) => setAbrir(e.target.checked)} />
            {t('components.PuestoGabinete.abrir_sola', 'Abrir la ficha sola al pasar a gabinete')}
          </label>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" data-guardar-gabinete="" onClick={listo} disabled={!elegido || ocupado}
            style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14, fontWeight: 600, cursor: !elegido || ocupado ? 'default' : 'pointer', opacity: !elegido || ocupado ? 0.6 : 1 }}>
            {t('components.PuestoGabinete.listo', 'Listo')}
          </button>
        </div>
      </div>
    </div>
  )
}
